import React from 'react';
import PropTypes from 'prop-types';
import Card from 'partial/components/Card';
import Table from 'partial/components/Table';
import Button from 'partial/components/Button';
import FormSelect from 'partial/form/FormSelect';
import { showDrawer } from 'partial/components/Modal';
import GenericInfo, {
  InfoItem,
  InfoItemCopy,
  InfoDivider,
} from 'partial/components/GenericInfo';
import { useApiKeyOptions } from '../hooks';

export const openTestApiModal = () => {
  showDrawer({
    size: 'lg',
    title: 'Test API',
    content: (onClose) => <TestApiModal onClose={onClose} />,
  });
};

function TestApiModal({ onClose }) {
  const [selected, setSelected] = React.useState('');
  const [apiKeyOptions] = useApiKeyOptions();
  const apiKey = React.useMemo(
    () => apiKeyOptions.find((x) => x.value === selected)?.data || {},
    [selected, apiKeyOptions]
  );
  React.useEffect(() => {
    if (!selected && apiKeyOptions[0]) {
      setSelected(apiKeyOptions[0].value);
    }
  }, [selected, setSelected, apiKeyOptions]);
  return (
    <div className="space-y-4">
      <div className="flex space-x-3 items-center">
        <label className="font-semibold" htmlFor="test-api">
          Select API Key
        </label>
        <FormSelect
          id="test-api"
          name="test-api"
          onSetFieldValue={(k, v) => setSelected(v)}
          options={apiKeyOptions}
          value={selected}
        />
      </div>
      <Card className="p-4">
        <div className="font-semibold text-gray-800 mb-4">API Key Details</div>
        <div className="-mx-4 -mb-4">
          <GenericInfo data={apiKey}>
            <InfoItem value="api_type" label="Key Type" />
            <InfoItem value="callback_url" label="Callback URL" />
            <InfoItemCopy
              value="token"
              label="Token"
              className="font-mono truncate"
            />
          </GenericInfo>
        </div>
      </Card>
      <Card className="p-4">
        <div className="font-semibold text-gray-800 mb-4">Integration</div>
        <div className="-mx-4 -mb-4">
          <GenericInfo data={apiKey}>
            <InfoItem
              value={() => 'http://sandbox.multipay.ph/generate-payment'}
              label="URL"
            />
            <InfoItem value={() => 'POST'} label="Method" />
            <InfoDivider label="Header Parameters" />
            <InfoItem
              value={() => '-API-TOKEN-HERE-'}
              label="X-MULTIPAY-TOKEN"
            />
            <InfoItem value={() => 'MERCHANT_CODE'} label="X-MULTIPAY-CODE" />
            <InfoDivider label="Body Parameters" />
            <div className="flex flex-col min-h-[360px]">
              <Table
                data={[
                  {
                    id: 1,
                    field: 'amount',
                    type: 'Double',
                    is_required: 'Yes',
                    description: 'Amount of the service',
                  },
                  {
                    id: 2,
                    field: 'txnid',
                    type: 'String',
                    is_required: 'Yes',
                    description: 'The transaction ID generated by the biller',
                  },
                  {
                    id: 3,
                    field: 'callback_url',
                    type: 'URL',
                    is_required: 'Yes',
                    description:
                      'The biller URL that will be communicated for every transaction',
                  },
                  {
                    id: 4,
                    field: 'digest',
                    type: 'String',
                    is_required: 'Yes',
                    description:
                      'The computed hash of the transaction that includes the token.\nsha1($amount$txnid$token)',
                  },
                  {
                    id: 5,
                    field: 'name',
                    type: 'String',
                    is_required: 'No',
                    description: 'The name of the transaction',
                  },
                  {
                    id: 6,
                    field: 'mobile',
                    type: 'String',
                    is_required: 'No',
                    description: 'Mobile number of the customer (for ereceipt)',
                  },
                  {
                    id: 7,
                    field: 'expires_at',
                    type: 'Date Time',
                    is_required: 'No',
                    description:
                      'The expiration of the transaction. E.g format\n(YYYY-MM-DD HH:MM:SS)',
                  },
                  {
                    id: 8,
                    field: 'description',
                    type: 'JSON Object',
                    is_required: 'No',
                    description: 'Additional information of the transaction',
                  },
                ]}
                format={[
                  { label: 'Field', key: 'field' },
                  { label: 'Type', key: 'type', width: 120 },
                  { label: 'Is Required', key: 'is_required', width: 120 },
                  { label: 'Description', key: 'description' },
                ]}
              />
            </div>
          </GenericInfo>
        </div>
      </Card>
      <Card className="p-4">
        <div className="font-semibold text-gray-800 mb-4">Tester</div>
        <div className="border-t -mx-4 -mb-4 p-4">
          <Button primary>Try Me</Button>
        </div>
      </Card>
      <div className="flex justify-center">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

TestApiModal.defaultProps = {
  // data: null
};

TestApiModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default TestApiModal;
